<!-- ═══════════════════════════════════════════
     BUBBLE (panel closed)
     ═══════════════════════════════════════════ -->
<ng-container *ngIf="!isOpen">

  <!-- Nudge tooltip (appears left of the tab) -->
  <div
    *ngIf="bubbleState === 'nudge' && nudgeMessage"
    class="fixed right-14 top-1/2 transform -translate-y-1/2 z-[90] nur-fade-in"
  >
    <div class="bg-white rounded-2xl shadow-xl border border-gray-100 px-4 py-3 max-w-[220px] relative">
      <button
        (click)="dismissNudge()"
        class="absolute top-2 right-2 p-0.5 text-gray-300 hover:text-gray-500 transition-colors"
        aria-label="Dismiss"
      >
        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <div class="flex items-start gap-2">
        <div class="w-5 h-5 bg-[#600b0b] rounded-full flex items-center justify-center shrink-0 mt-0.5">
          <span class="text-white text-[8px]">✦</span>
        </div>
        <p class="text-[12px] text-gray-700 leading-relaxed pr-4">{{ nudgeMessage }}</p>
      </div>
      <!-- Arrow pointing right toward tab -->
      <div
        class="absolute top-1/2 -right-[7px] -translate-y-1/2 w-3.5 h-3.5 bg-white border-r border-t border-gray-100 rotate-45"
        style="box-shadow: 2px -1px 3px rgba(0,0,0,0.04)"
      ></div>
    </div>
  </div>

  <!-- Side tab (right edge, vertically centered) -->
  <button
    (click)="openPanel()"
    class="nur-side-tab"
    [class.nur-pulse-tab]="bubbleState === 'nudge'"
    aria-label="Open NUR \u2014 Your Wall Decor Assistant"
  >
    <span class="text-[14px]">✦</span>
    <span class="text-[9px] font-bold uppercase tracking-[0.15em]">NUR</span>
  </button>

</ng-container>


<!-- ═══════════════════════════════════════════
     PANEL (open)
     ═══════════════════════════════════════════ -->
<ng-container *ngIf="isOpen">

  <!-- Backdrop -->
  <div
    class="fixed inset-0 bg-black bg-opacity-10 z-[90] transition-opacity duration-300"
    (click)="close()"
  ></div>

  <!-- Panel -->
  <div
    class="fixed right-0 top-0 h-full z-[95] bg-white shadow-2xl flex flex-col transition-all duration-300 ease-in-out nur-panel-slide"
    [ngStyle]="{'width': panelState === 'expanded' ? '52vw' : '420px', 'max-width': panelState === 'expanded' ? '680px' : '92vw'}"
    (dragover)="onDragOver($event)"
    (drop)="onDrop($event)"
  >

    <!-- ═══ HEADER ═══ -->
    <div class="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 shrink-0 bg-white">
      <div class="flex items-center gap-3">
        <div class="w-8 h-8 bg-[#600b0b] rounded-full flex items-center justify-center shadow-sm">
          <span class="text-white text-[12px]">✦</span>
        </div>
        <div>
          <p class="text-[13px] font-bold text-gray-900 tracking-tight">Nur</p>
          <p class="text-[9px] text-gray-400 font-medium uppercase tracking-widest">Your Wall Decor Guide</p>
        </div>
      </div>
      <div class="flex items-center gap-1.5">
        <button
          (click)="reset()"
          class="px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-gray-400 hover:text-[#600b0b] hover:bg-[#600b0b]/5 rounded-lg transition-all"
        >Reset</button>
        <button
          (click)="toggleSize()"
          class="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-all"
          [attr.aria-label]="panelState === 'expanded' ? 'Minimize' : 'Expand'"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <ng-container *ngIf="panelState !== 'expanded'">
              <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
            </ng-container>
            <ng-container *ngIf="panelState === 'expanded'">
              <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/>
            </ng-container>
          </svg>
        </button>
        <button
          (click)="close()"
          class="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all"
          aria-label="Close"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    </div>

    <!-- ═══ ACTIVE FILTER PILLS (removable) ═══ -->
    <div
      *ngIf="activeFilterCount > 0 || wallSize"
      class="flex flex-wrap items-center gap-1.5 px-4 py-2 border-b border-gray-50 bg-gray-50/50"
    >
      <span class="text-[8px] font-bold uppercase tracking-widest text-gray-400 mr-1">Filters</span>
      <!-- Room -->
      <button *ngIf="filters.room" (click)="removeFilter('room')"
        class="inline-flex items-center gap-1 bg-[#600b0b]/10 text-[#600b0b] text-[10px] font-semibold pl-2.5 pr-1.5 py-0.5 rounded-full hover:bg-[#600b0b]/20 transition-colors group">
        {{ filters.room }}
        <svg class="w-3 h-3 opacity-50 group-hover:opacity-100" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <!-- Style -->
      <button *ngIf="filters.style" (click)="removeFilter('style')"
        class="inline-flex items-center gap-1 bg-[#600b0b]/10 text-[#600b0b] text-[10px] font-semibold pl-2.5 pr-1.5 py-0.5 rounded-full hover:bg-[#600b0b]/20 transition-colors group">
        {{ filters.style }}
        <svg class="w-3 h-3 opacity-50 group-hover:opacity-100" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <!-- Mood -->
      <button *ngIf="filters.mood" (click)="removeFilter('mood')"
        class="inline-flex items-center gap-1 bg-[#600b0b]/10 text-[#600b0b] text-[10px] font-semibold pl-2.5 pr-1.5 py-0.5 rounded-full hover:bg-[#600b0b]/20 transition-colors group">
        {{ filters.mood }}
        <svg class="w-3 h-3 opacity-50 group-hover:opacity-100" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <!-- Color -->
      <button *ngIf="filters.color" (click)="removeFilter('color')"
        class="inline-flex items-center gap-1 bg-[#600b0b]/10 text-[#600b0b] text-[10px] font-semibold pl-2.5 pr-1.5 py-0.5 rounded-full hover:bg-[#600b0b]/20 transition-colors group">
        {{ filters.color }}
        <svg class="w-3 h-3 opacity-50 group-hover:opacity-100" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <!-- Wall size -->
      <button *ngIf="wallSize" (click)="removeFilter('wallSize')"
        class="inline-flex items-center gap-1 bg-[#600b0b]/10 text-[#600b0b] text-[10px] font-semibold pl-2.5 pr-1.5 py-0.5 rounded-full hover:bg-[#600b0b]/20 transition-colors group">
        {{ wallSize }}
        <svg class="w-3 h-3 opacity-50 group-hover:opacity-100" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <!-- Keywords -->
      <button *ngIf="searchKeywords" (click)="removeFilter('keywords')"
        class="inline-flex items-center gap-1 bg-[#c49a6c]/20 text-[#8c1c1c] text-[10px] font-semibold pl-2.5 pr-1.5 py-0.5 rounded-full hover:bg-[#c49a6c]/30 transition-colors group">
        🔍 {{ searchKeywords }}
        <svg class="w-3 h-3 opacity-50 group-hover:opacity-100" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <!-- Clear all -->
      <button *ngIf="activeFilterCount > 1" (click)="clearAllFilters()"
        class="text-[9px] font-bold text-gray-400 hover:text-red-500 ml-1 transition-colors">
        Clear all
      </button>
    </div>

    <!-- ═══ UPLOAD ANALYSIS PANEL ═══ -->
    <div *ngIf="activeImage" class="shrink-0 border-b border-gray-100">
      <!-- Image preview -->
      <div class="relative bg-black">
        <img [src]="activeImage" [alt]="activeUploadType === 'wall' ? 'Your wall' : 'Reference image'" class="w-full h-48 object-cover opacity-90">
        <!-- Wallpaper overlay preview -->
        <div *ngIf="previewWallpaper && activeUploadType === 'wall'" class="absolute inset-0 flex items-center justify-center">
          <img [src]="previewWallpaper.image" class="w-3/5 h-3/5 object-cover opacity-80 rounded-lg shadow-lg">
        </div>
        <button
          (click)="removeUpload()"
          class="absolute top-3 right-3 w-7 h-7 bg-black bg-opacity-60 rounded-full flex items-center justify-center text-white hover:bg-opacity-80 transition-colors"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div class="absolute bottom-3 left-3">
          <span class="text-[10px] font-bold uppercase tracking-wider bg-[#600b0b] text-white px-2 py-1 rounded-lg">
            {{ activeUploadType === 'wall' ? 'Your wall' : 'Reference image' }}
          </span>
        </div>
      </div>
      <!-- Analysis loading -->
      <div *ngIf="isAnalyzing" class="flex items-center gap-3 px-4 py-3">
        <div class="w-7 h-7 bg-[#600b0b]/10 rounded-full flex items-center justify-center shrink-0">
          <svg class="w-3.5 h-3.5 text-[#600b0b] animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
        </div>
        <div>
          <p class="text-[12px] font-medium text-gray-800">
            {{ activeUploadType === 'wall' ? 'Analysing your wall\u2026' : 'Finding visual matches\u2026' }}
          </p>
          <p class="text-[10px] text-gray-400 mt-0.5">Just a moment</p>
        </div>
      </div>
    </div>

    <!-- ═══ SCROLLABLE CHAT AREA ═══ -->
    <div #scrollRef class="flex-1 overflow-y-auto px-4 py-3 min-h-0 nur-scroll-area">

      <div class="space-y-3">

        <!-- Chat Messages -->
        <ng-container *ngFor="let msg of chatHistory; let i = index">

          <!-- Message bubble -->
          <div class="flex items-end gap-2 nur-fade-in"
               [class.justify-end]="msg.role === 'user'"
               [class.justify-start]="msg.role === 'assistant'">
            <!-- NUR avatar -->
            <div *ngIf="msg.role === 'assistant'" class="w-5 h-5 bg-[#600b0b] rounded-full flex items-center justify-center shrink-0 mb-0.5">
              <span class="text-white text-[7px]">✦</span>
            </div>
            <!-- Bubble -->
            <div class="max-w-[82%] px-3 py-2 text-[12px] leading-relaxed"
                 [ngClass]="msg.role === 'user'
                   ? 'bg-[#600b0b] text-white rounded-2xl rounded-br-sm'
                   : 'nur-msg-assistant rounded-2xl rounded-bl-sm'">
              {{ msg.content }}
            </div>
          </div>

          <!-- Inline SKU strip -->
          <div *ngIf="msg.role === 'assistant' && msg.resultIds && msg.resultIds.length > 0" class="ml-7 mt-2 nur-fade-in">
            <p class="text-[9px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">
              {{ msg.resultIds.length }} matching wallpaper{{ msg.resultIds.length !== 1 ? 's' : '' }}
            </p>
            <div class="flex gap-2 overflow-x-auto pb-1 -mx-0.5 px-0.5 scrollbar-hide">
              <ng-container *ngFor="let id of msg.resultIds.slice(0, 8)">
                <button
                  *ngIf="getWallpaper(id) as wp"
                  (click)="onProductClick(id)"
                  class="shrink-0 w-[72px] text-left group focus:outline-none"
                >
                  <div class="w-[72px] h-[56px] rounded-lg overflow-hidden border border-gray-100 group-hover:border-[#600b0b]/40 group-hover:shadow-sm transition-all">
                    <img [src]="wp.list_thumbnail || wp.hover_thumbnail" [alt]="wp.name" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300">
                  </div>
                  <p class="text-[7.5px] text-[#600b0b] font-mono mt-0.5 truncate">{{ wp.wallpaper_id }}</p>
                  <p class="text-[9px] text-gray-600 truncate leading-tight">{{ wp.name }}</p>
                </button>
              </ng-container>
              <!-- +N more -->
              <div
                *ngIf="msg.resultIds.length > 8"
                class="shrink-0 w-[52px] flex flex-col items-center justify-center text-[9px] text-gray-400 gap-0.5 border border-dashed border-gray-200 rounded-lg"
              >
                <span class="font-bold text-[11px] text-gray-500">+{{ msg.resultIds.length - 8 }}</span>
                <span>more</span>
              </div>
            </div>
          </div>

          <!-- Chips + follow-up (on LAST assistant message only, when not loading) -->
          <div
            *ngIf="msg.role === 'assistant' && isLastAssistant(i) && !isAiLoading && guidance && (guidance.chips?.length || guidance.followUp)"
            class="ml-7 mt-2.5 space-y-2 nur-fade-in"
          >
            <p *ngIf="guidance.followUp" class="text-[11px] font-semibold text-gray-900 px-0.5">{{ guidance.followUp }}</p>
            <div *ngIf="guidance.chips?.length" class="flex flex-wrap gap-1.5">
              <button
                *ngFor="let chip of guidance.chips"
                (click)="handleGuidanceChip(chip.id)"
                class="px-3 py-1.5 bg-white text-[#600b0b] text-[10px] font-semibold rounded-xl border border-[#600b0b]/25 hover:bg-[#600b0b] hover:text-white hover:border-[#600b0b] transition-all shadow-sm"
              >{{ chip.label }}</button>
            </div>
          </div>

        </ng-container>

        <!-- Loading dots -->
        <div *ngIf="isAiLoading" class="flex items-end gap-2 justify-start">
          <div class="w-5 h-5 bg-[#600b0b] rounded-full flex items-center justify-center shrink-0 mb-0.5">
            <span class="text-white text-[7px]">✦</span>
          </div>
          <div class="nur-msg-assistant rounded-2xl rounded-bl-sm px-3 py-2.5 flex gap-1">
            <span class="w-1.5 h-1.5 bg-[#600b0b] bg-opacity-50 rounded-full animate-bounce" style="animation-delay: 0ms"></span>
            <span class="w-1.5 h-1.5 bg-[#600b0b] bg-opacity-50 rounded-full animate-bounce" style="animation-delay: 150ms"></span>
            <span class="w-1.5 h-1.5 bg-[#600b0b] bg-opacity-50 rounded-full animate-bounce" style="animation-delay: 300ms"></span>
          </div>
        </div>

      </div>

    </div>

    <!-- ═══ INPUT BAR ═══ -->
    <div class="flex items-center gap-2 px-4 py-3 border-t border-gray-100 bg-white shrink-0">
      <!-- Camera button -->
      <button
        (click)="wallFileInput.click()"
        class="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-50 text-gray-400 hover:bg-[#600b0b]/5 hover:text-[#600b0b] transition-all shrink-0"
        aria-label="Upload photo"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/>
        </svg>
      </button>
      <!-- Text input -->
      <input
        [(ngModel)]="inputValue"
        (keydown.enter)="send()"
        placeholder="Describe your ideal wallpaper..."
        class="flex-1 px-3 py-2 text-[12px] bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:border-[#600b0b]/30 focus:ring-1 focus:ring-[#600b0b]/10 transition-all placeholder-gray-400"
      >
      <!-- Send button -->
      <button
        (click)="send()"
        [disabled]="!inputValue.trim() || isAiLoading"
        class="w-9 h-9 flex items-center justify-center rounded-xl transition-all shrink-0"
        [ngClass]="inputValue.trim() && !isAiLoading
          ? 'bg-[#600b0b] text-white hover:bg-[#7a1010] shadow-sm'
          : 'bg-gray-100 text-gray-300 cursor-not-allowed'"
        aria-label="Send"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
      </button>
    </div>

  </div>

</ng-container>


<!-- Hidden file inputs -->
<input #wallFileInput type="file" accept="image/*" hidden (change)="handleWallUpload($event)">
<input #refFileInput type="file" accept="image/*" hidden (change)="handleRefUpload($event)">
